import React, { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { ScrollView, StyleSheet } from 'react-native';

import { useNavbarContainerHeight, useResponsive, useToggleState } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEventOnFocus } from '@order/Telemetry';

import { AppFooter, LoadingAnimation } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { GiftCardsErrorView, GiftCardsSignedOutModal, GiftCardsWrapper } from './components';
import { useGiftCards } from './hooks';
import {
  GiftCardsScreenCategoriesGrid,
  GiftCardsScreenCategoriesNav,
  GiftCardsScreenHeader,
} from './sections';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardsScreen = () => {
  const { data, error, fetching } = useGiftCards();
  const { heading, subheading, subheadingLinkText, giftCardCategories } = data;

  const router = useRouter();

  const { track } = useTelemetry();
  const isSignedIn = useIsLoggedIn();
  const { currentBreakpoint } = useResponsive();
  const { paddingBottomStyle: navbarPaddingBottomStyle } = useNavbarContainerHeight();
  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled'); // prettier-ignore
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  // ─── State ───────────────────────────────────────────────────────────

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  const { value: isRedeemGiftCardSignedOutModalShown, toggle: toggleRedeemGiftCardSignedOutModal } =
    useToggleState(false);
  const { value: isGiftCardSignedOutModalShown, toggle: toggleGiftCardSignedOutModal } =
    useToggleState(false);

  // ─── Derived Data ────────────────────────────────────────────────────

  const filteredCategories = useMemo(
    () =>
      giftCardCategories.filter((category) => {
        const { id: categoryId } = category.fields;

        return !selectedCategoryId || categoryId === selectedCategoryId;
      }),
    [giftCardCategories, selectedCategoryId],
  );

  const hasCategories = filteredCategories.length > 0;
  const shouldRenderGiftCardsList = !fetching;
  const shouldApplyNavbarPadding = isNavbarV2Enabled && currentBreakpoint.isXS;
  const contentContainerStyles = [
    styles.contentContainer,
    shouldApplyNavbarPadding ? navbarPaddingBottomStyle : undefined,
  ];

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToAddGiftCardScreen = useCallback(() => {
    track('gift.redeem_cta_tapped');

    if (!isSignedIn) {
      toggleRedeemGiftCardSignedOutModal();

      return;
    }

    if (currentBreakpoint.isXS) {
      router.navigate('/add-gift-card');
      return;
    }

    if (isInStoreOrdersEnabled) {
      router.navigate('/account/gift-cards');
      return;
    }

    router.navigate('/account/payment-and-gift-cards');
  }, [
    track,
    isSignedIn,
    currentBreakpoint.isXS,
    isInStoreOrdersEnabled,
    router,
    toggleRedeemGiftCardSignedOutModal,
  ]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventOnFocus({ name: 'gift.view.success' }, { skip: !shouldRenderGiftCardsList });

  // ─────────────────────────────────────────────────────────────────────

  if (fetching) {
    return <LoadingAnimation size="large" />;
  }

  return (
    <GiftCardsWrapper>
      <ScrollView stickyHeaderIndices={[1]} contentContainerStyle={contentContainerStyles}>
        <GiftCardsScreenHeader
          heading={heading}
          subheading={subheading}
          subheadingLinkText={subheadingLinkText}
          opensDialog={!isSignedIn}
          onSubheadingLinkPress={navigateToAddGiftCardScreen}
        />

        {hasCategories ? (
          <GiftCardsScreenCategoriesNav
            categories={giftCardCategories}
            selectedCategoryId={selectedCategoryId}
            setSelectedCategoryId={setSelectedCategoryId}
          />
        ) : null}

        {error ? <GiftCardsErrorView /> : null}

        {hasCategories ? (
          <GiftCardsScreenCategoriesGrid
            categories={filteredCategories}
            isSignedIn={isSignedIn}
            onSignedOutCardPress={toggleGiftCardSignedOutModal}
          />
        ) : null}

        {currentBreakpoint.isXS ? null : <AppFooter />}
      </ScrollView>

      <GiftCardsSignedOutModal
        isVisible={isRedeemGiftCardSignedOutModalShown}
        toggleModal={toggleRedeemGiftCardSignedOutModal}
        redirectTo="/account/payment-and-gift-cards"
      />

      <GiftCardsSignedOutModal
        isVisible={isGiftCardSignedOutModalShown}
        toggleModal={toggleGiftCardSignedOutModal}
        redirectTo="/account/gift-cards"
      />
    </GiftCardsWrapper>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentContainer: {
    minHeight: '100%',
  },
});
